import { isDefined } from "@clipboard-health/util-ts";
import { CardActionArea, Stack } from "@mui/material";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { parseISO } from "date-fns";
import { first } from "lodash";
import { generatePath, useHistory } from "react-router-dom";

import { RootPaths } from "../App/paths";
import {
  ShiftRateNegotiationRole,
  ShiftRateNegotiationStatus,
} from "../OpenShifts/RateNegotiation/types";
import { useGetQualificationDisplayLabels } from "../Qualifications/api/useGetQualificationDisplayLabels";
import { calculateMinutesElapsed } from "../redesign/ArrivalTime/calculateMinutesElapsed";
import { asTimeSlot, isShiftNameType } from "../redesign/PlacementCandidate/types";
import { ShiftCardWrapper } from "../redesign/Shift/Card/Wrapper";
import { BookingCardContentWrapper } from "../redesign/Shift/CardContentWrapper";
import { useShiftDurationWithBreak } from "../redesign/Shift/hooks/useShiftDurationWithBreak";
import { ShiftCardSummaryInfo } from "../redesign/Shift/Open/CardSummaryInfo";
import { ShiftQualificationLabel } from "../redesign/Shift/QualificationLabel";
import { TimeSlotPill } from "../redesign/Shift/TimeSlotPill";
import {
  SHIFT_DISCOVERY_LIST_PATH,
  SHIFT_DISCOVERY_SHIFT_NEGOTIATION_MODAL_PATH,
} from "../redesign/ShiftDiscovery/paths";
import { useDistanceToWorkplace } from "../redesign/Workplace/useDistanceToWorkplace";
import { type WorkerRateNegotiation } from "./api/useWorkerNegotiationShifts";

interface RateNegotiationListItemProps {
  workerNegotiationShift: WorkerRateNegotiation;
}

function getNegotiationStatusText(parameters: {
  status: ShiftRateNegotiationStatus;
  lastOfferBy: ShiftRateNegotiationRole | undefined;
}) {
  const { status, lastOfferBy } = parameters;

  if (
    status === ShiftRateNegotiationStatus.OPEN &&
    lastOfferBy === ShiftRateNegotiationRole.WORKER
  ) {
    return "Proposal pending";
  }

  if (
    status === ShiftRateNegotiationStatus.OPEN &&
    lastOfferBy === ShiftRateNegotiationRole.WORKPLACE
  ) {
    return "Proposal waiting for reply";
  }

  if (status === ShiftRateNegotiationStatus.EXPIRED) {
    return "Proposal expired";
  }

  if (status === ShiftRateNegotiationStatus.REJECTED) {
    return "Proposal rejected";
  }

  if (status === ShiftRateNegotiationStatus.CLOSED) {
    return "Proposal closed";
  }

  return undefined;
}

export function RateNegotiationListItem(props: RateNegotiationListItemProps) {
  const history = useHistory();

  const { workerNegotiationShift } = props;
  const {
    _id: shiftId,
    start,
    end,
    name: timeSlot,
    agentReq: qualification,
    facility: workplace,
    breakPolicy,
    rateNegotiation: {
      _id: rateNegotiationId,
      status,
      metadata: { lastOffer },
    },
  } = workerNegotiationShift;

  const { data: qualificationDisplayLabels, isLoading: isLoadingQualification } =
    useGetQualificationDisplayLabels([qualification]);
  const qualificationText = first(qualificationDisplayLabels);

  const startDate = parseISO(start);
  const endDate = parseISO(end);

  const minutesElapsed = calculateMinutesElapsed(startDate, new Date());

  const { totalDurationInHours, mandatoryBreakDurationInMinutes, requiresBreak } =
    useShiftDurationWithBreak({
      startDate,
      endDate,
      requiresLunchBreak: workplace.requiresLunchBreak,
      breakPolicy,
      minutesElapsed,
    });

  const workplaceGeoLocation = {
    longitude: workplace.geoLocation.coordinates[0],
    latitude: workplace.geoLocation.coordinates[1],
  };

  const { formattedDistance } = useDistanceToWorkplace({
    workplaceGeoLocation,
  });

  const timeSlotPill = isShiftNameType(timeSlot) && asTimeSlot(timeSlot);
  const negotiationStatusText = getNegotiationStatusText({
    status,
    lastOfferBy: lastOffer?.createdByUserRole,
  });

  return (
    <ShiftCardWrapper
      sx={{ display: "flex", marginBottom: 5 }}
      data-testid={`shift-card-${shiftId}`}
      isUrgent={false}
      onClick={() => {
        const basePath = `${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}`;
        const modalPath = generatePath(
          `${basePath}/${SHIFT_DISCOVERY_SHIFT_NEGOTIATION_MODAL_PATH}`,
          {
            shiftId,
          }
        );
        history.push(`${modalPath}?rateNegotiationId=${rateNegotiationId}`);
      }}
    >
      <CardActionArea sx={{ flexGrow: 1 }}>
        <BookingCardContentWrapper sx={{ gap: 5 }}>
          {timeSlotPill && <TimeSlotPill timeSlot={timeSlotPill} />}
          <ShiftCardSummaryInfo
            sx={{ flex: 1 }}
            dateRange={{
              startDate: parseISO(start),
              endDate: parseISO(end),
            }}
            timezone={workplace.tmz}
            deviceTimezone={new Intl.DateTimeFormat().resolvedOptions().timeZone}
            durationInHours={totalDurationInHours}
            breakDurationInMinutes={mandatoryBreakDurationInMinutes}
            requiresBreak={requiresBreak}
            workplaceName={workplace.name}
            formattedDistance={formattedDistance}
            showBreakInfo={false}
            minutesElapsed={minutesElapsed}
          />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <ShiftQualificationLabel
              qualificationText={qualificationText ?? ""}
              isLoading={isLoadingQualification}
            />

            {isDefined(negotiationStatusText) && (
              <Text semibold variant="body2">
                {negotiationStatusText}
              </Text>
            )}
          </Stack>
        </BookingCardContentWrapper>
      </CardActionArea>
    </ShiftCardWrapper>
  );
}
